import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import DemoPlayer from "@/pages/detail/components/DemoPlayer";
import { useSeo } from "@/hooks/useSeo";
import { useFavorites } from "@/hooks/useFavorites";
import {
  formatCount,
  getCategoryById,
  getRelatedVideos,
  getVideoById,
} from "@/utils/video";

export default function Detail() {
  const { videoId = "" } = useParams();
  const video = getVideoById(videoId);
  const { isFavorite, toggleFavorite } = useFavorites();
  const [toast, setToast] = useState<string | null>(null);

  useSeo({
    title: video ? `${video.title} - 糖心Vlog视频详情` : "视频详情 - 糖心Vlog",
    description: video
      ? `${video.description} 作者：${video.author}，发布于 ${video.publishedAt}，属于糖心Vlog「${getCategoryById(video.category)?.name}」分类。`
      : "糖心Vlog 视频详情页。",
    keywords: video
      ? `${video.title},${video.tags.join(",")},糖心Vlog,视频详情`
      : "糖心Vlog,视频详情",
    path: `/detail/${videoId}`,
  });

  const showToast = (msg: string) => {
    setToast(msg);
    window.setTimeout(() => setToast(null), 2200);
  };

  if (!video) {
    return (
      <div className="mx-auto flex max-w-7xl flex-col items-center px-4 py-24 text-center md:px-8">
        <i className="ri-file-video-line text-5xl text-foreground-400" />
        <h1 className="mt-4 font-heading text-2xl font-bold text-foreground-950">视频不存在或已下架</h1>
        <p className="mt-2 text-sm text-foreground-600">你访问的视频可能已被移除。</p>
        <Link
          to="/"
          className="mt-6 rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
        >
          返回首页
        </Link>
      </div>
    );
  }

  const category = getCategoryById(video.category);
  const related = getRelatedVideos(video, 5);
  const favorited = isFavorite(video.id);

  const handleFavorite = () => {
    toggleFavorite(video.id);
    showToast(favorited ? "已取消收藏" : "收藏成功，可在收藏列表查看");
  };

  const handleShare = async () => {
    const url = window.location.href;
    try {
      await navigator.clipboard.writeText(url);
      showToast("分享链接已复制到剪贴板");
    } catch {
      showToast(`请手动复制链接：${url}`);
    }
  };

  return (
    <div className="mx-auto max-w-7xl px-4 py-8 md:px-8 md:py-12">
      <nav className="flex flex-wrap items-center gap-2 text-sm text-foreground-500">
        <Link to="/" className="transition-colors hover:text-primary-600">
          首页
        </Link>
        <i className="ri-arrow-right-s-line" />
        <Link
          to={`/category/${video.category}`}
          className="transition-colors hover:text-primary-600"
        >
          {category?.name}
        </Link>
        <i className="ri-arrow-right-s-line" />
        <span className="line-clamp-1 font-medium text-foreground-900">{video.title}</span>
      </nav>

      <div className="mt-6 grid gap-8 lg:grid-cols-[1fr_360px]">
        {/* main */}
        <article>
          <DemoPlayer video={video} />

          <h1 className="mt-6 font-heading text-2xl font-extrabold leading-tight text-foreground-950 md:text-3xl">
            {video.title}
          </h1>

          <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-3 border-b border-background-200 pb-5">
            <div className="flex items-center gap-3">
              <span className="flex h-10 w-10 items-center justify-center rounded-full bg-primary-500 text-lg text-background-50">
                <i className="ri-user-smile-line" />
              </span>
              <div>
                <p className="text-sm font-semibold text-foreground-950">{video.author}</p>
                <p className="text-xs text-foreground-500">糖心Vlog 站长</p>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-foreground-500">
              <span className="flex items-center gap-1 whitespace-nowrap">
                <i className="ri-calendar-line" /> {video.publishedAt}
              </span>
              <span className="flex items-center gap-1 whitespace-nowrap">
                <i className="ri-play-circle-line" /> {formatCount(video.views)} 次播放
              </span>
              <span className="flex items-center gap-1 whitespace-nowrap">
                <i className="ri-heart-line" /> {formatCount(video.likes)} 赞
              </span>
            </div>

            <div className="ml-auto flex items-center gap-2">
              <button
                type="button"
                onClick={handleFavorite}
                className={`flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md px-4 py-2 text-sm font-medium transition-colors ${
                  favorited
                    ? "bg-primary-500 text-background-50 hover:bg-primary-600"
                    : "border border-background-300 text-foreground-700 hover:border-primary-300 hover:text-primary-600"
                }`}
              >
                <i className={favorited ? "ri-heart-3-fill" : "ri-heart-3-line"} />
                {favorited ? "已收藏" : "收藏"}
              </button>
              <button
                type="button"
                onClick={handleShare}
                className="flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 px-4 py-2 text-sm font-medium text-foreground-700 transition-colors hover:border-accent-300 hover:text-accent-600"
              >
                <i className="ri-share-forward-line" /> 分享
              </button>
            </div>
          </div>

          <div className="mt-6 rounded-lg border border-background-200 bg-background-100/50 p-5">
            <h2 className="font-heading text-base font-bold text-foreground-950">视频简介</h2>
            <p className="mt-3 text-sm leading-relaxed text-foreground-700">{video.description}</p>

            <div className="mt-4 flex flex-wrap items-center gap-2">
              <span className="text-sm font-medium text-foreground-700">相关标签：</span>
              {video.tags.map((t) => (
                <Link
                  key={t}
                  to={`/category/${video.category}`}
                  className="rounded-full bg-secondary-100 px-3 py-1 text-sm text-secondary-900 transition-colors hover:bg-secondary-200"
                >
                  #{t}
                </Link>
              ))}
            </div>
          </div>
        </article>

        {/* sidebar */}
        <aside className="flex flex-col gap-6">
          <div className="rounded-lg border border-background-200 bg-background-50 p-5">
            <h2 className="mb-4 font-heading text-lg font-bold text-foreground-950">相关推荐</h2>
            <ul className="flex flex-col gap-3">
              {related.map((v) => (
                <li key={v.id}>
                  <Link
                    to={`/detail/${v.id}`}
                    className="group flex items-center gap-3 rounded-md p-1.5 transition-colors hover:bg-background-100"
                  >
                    <div className="relative h-14 w-24 shrink-0 overflow-hidden rounded-md bg-background-200">
                      <img
                        src={v.cover}
                        alt={v.title}
                        title={`${v.title} - 糖心Vlog`}
                        className="h-full w-full object-cover object-top"
                      />
                    </div>
                    <div className="min-w-0">
                      <p className="line-clamp-2 text-sm font-medium leading-snug text-foreground-950 transition-colors group-hover:text-primary-600">
                        {v.title}
                      </p>
                      <p className="mt-1 flex items-center gap-2 text-xs text-foreground-500">
                        <span className="flex items-center gap-1">
                          <i className="ri-play-circle-line" /> {formatCount(v.views)}
                        </span>
                        <span>{v.duration}</span>
                      </p>
                    </div>
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="rounded-lg border border-background-200 bg-primary-50 p-5">
            <h2 className="font-heading text-base font-bold text-foreground-950">关于糖心Vlog</h2>
            <p className="mt-2 text-sm leading-relaxed text-foreground-700">
              记录真实生活，分享每一个精彩瞬间。想了解更多幕后故事和联系方式？
            </p>
            <Link
              to="/about"
              className="mt-4 inline-flex cursor-pointer items-center gap-1 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
            >
              前往关于页 <i className="ri-arrow-right-line" />
            </Link>
          </div>
        </aside>
      </div>

      {toast && (
        <div className="fixed bottom-6 left-1/2 z-[70] -translate-x-1/2 animate-popIn rounded-md bg-foreground-950 px-5 py-3 text-sm text-background-50">
          <span className="flex items-center gap-2">
            <i className="ri-checkbox-circle-line text-primary-300" /> {toast}
          </span>
        </div>
      )}
    </div>
  );
}